import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { fetchFlaggedRecordsData } from '../../services/leadManagementApi';
import axios from 'axios';
import { withRetry } from '../../lib/utils/withRetry';
import { withLmaSafeApiLogging } from '../../lib/utils/lmaSafeLogging';
import { LMADropdownResponse } from '../../components/LMA/types';

const objectToQueryString = (obj: any) => {
  const keys = Object.keys(obj);
  const keyValuePairs = keys.map((key) => {
    return encodeURIComponent(key) + '=' + encodeURIComponent(obj[key]);
  });
  return keyValuePairs.join('&');
};

export interface FlaggedRecordsState {
  data: any[];
  totalRecords: number;
  loading: boolean;
  error: string | null;
  lastUpdated: string | null;
  currentPage: number;
  pageSize: number;
  filters: Record<string, string>;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  dropdownData: LMADropdownResponse | null;
  dropdownLoading: boolean;
  dropdownError: string | null;
}

const initialState: FlaggedRecordsState = {
  data: [],
  totalRecords: 0,
  loading: false,
  error: null,
  lastUpdated: null,
  currentPage: 1,
  pageSize: 10,
  filters: {},
  sortBy: undefined,
  sortOrder: undefined,
  dropdownData: null,
  dropdownLoading: false,
  dropdownError: null,
};

// Async thunk for fetching flagged records data with filters and pagination
export const fetchFlaggedRecords = createAsyncThunk(
  'flaggedRecords/GetFlaggedRecordsData',
  async (data: any, { rejectWithValue, dispatch }) => {
    try {
      const response = await fetchFlaggedRecordsData(data.params, data.payload);
      return response;
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message ||
        'Unable to load flagged records data. Please reload the page or contact IT support if the issue persists.';
      dispatch({
        type: 'toast/showToast',
        payload: { message: errorMessage, severity: 'error' },
      });
      return rejectWithValue(errorMessage);
    }
  }
);

// Async thunk for fetching dropdown data for flagged records tab
export const fetchFlaggedRecordsDropdownData = createAsyncThunk(
  'flaggedRecords/fetchDropdownData',
  async (leadStatusFilters: any[], { rejectWithValue, dispatch }) => {
    try {
      let hotLeadMin = 0;
      let coldLeadMax = 0;

      if (leadStatusFilters && Array.isArray(leadStatusFilters)) {
        leadStatusFilters.forEach((filter: any) => {
          const name = filter.name?.toLowerCase();
          const lowerLimit = filter.fields?.LSFDaysLowerLimit?.value;
          const upperLimit = filter.fields?.LSFDaysUpperLimit?.value;

          if (name && lowerLimit !== undefined) {
            if (name === 'hot' && upperLimit !== undefined) {
              hotLeadMin = lowerLimit;
            } else if (name === 'cold') {
              coldLeadMax = upperLimit;
            }
          }
        });
      }

      const params = {
        LeadStatus: 'flagged',
        leadType: 'referral',
        HotLeadMin: hotLeadMin,
        ColdLeadMax: coldLeadMax,
      };

      const response = await withLmaSafeApiLogging(
        {
          operation: 'fetchFlaggedRecordsDropdownData',
          method: 'GET',
          endpoint: '/LMA/GetLMAFilterDropdownValues',
        },
        () =>
          withRetry(() =>
            axios.get(
              `/api/dap-api?url=/LMA/GetLMAFilterDropdownValues&${objectToQueryString(params)}`
            )
          )
      );
      return response?.data as LMADropdownResponse;
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message ||
        'Unable to load filter options. Please reload the page or contact IT support if the issue persists.';
      dispatch({
        type: 'toast/showToast',
        payload: { message: errorMessage, severity: 'error' },
      });
      return rejectWithValue(errorMessage);
    }
  }
);

const flaggedRecordsSlice = createSlice({
  name: 'flaggedRecords',
  initialState,
  reducers: {
    updateFlaggedRecord: (state, action: PayloadAction<{ id: string; updates: any }>) => {
      const index = state.data.findIndex((item) => item.id === action.payload.id);
      if (index !== -1) {
        state.data[index] = { ...state.data[index], ...action.payload.updates };
      }
    },
    deleteFlaggedRecord: (state, action: PayloadAction<string>) => {
      const initialLength = state.data.length;
      state.data = state.data.filter((item) => item.id !== action.payload);
      const removed = initialLength - state.data.length;
      if (removed > 0) {
        state.totalRecords = Math.max(0, state.totalRecords - removed);
      }
    },
    deleteFlaggedRecordByPatientId: (state, action: PayloadAction<string>) => {
      const initialLength = state.data.length;
      state.data = state.data.filter((item) => item.patientId !== action.payload);
      const removed = initialLength - state.data.length;
      if (removed > 0) {
        state.totalRecords = Math.max(0, state.totalRecords - removed);
      }
    },
    addFlaggedRecord: (state, action: PayloadAction<any>) => {
      state.data.push(action.payload);
    },
    setFilters: (state, action: PayloadAction<Record<string, string>>) => {
      state.filters = action.payload;
      state.currentPage = 1; // Reset to page 1 when filters change
    },
    setPage: (state, action: PayloadAction<number>) => {
      state.currentPage = action.payload;
    },
    setPageSize: (state, action: PayloadAction<number>) => {
      state.pageSize = action.payload;
      state.currentPage = 1; // Reset to page 1 when page size changes
    },
    setSorting: (state, action: PayloadAction<{ sortBy?: string; sortOrder?: 'asc' | 'desc' }>) => {
      state.sortBy = action.payload.sortBy;
      state.sortOrder = action.payload.sortOrder;
    },
    clearFlaggedRecordsData: (state) => {
      state.data = [];
      state.totalRecords = 0;
      state.error = null;
      state.lastUpdated = null;
      state.filters = {};
      state.currentPage = 1;
      state.sortBy = undefined;
      state.sortOrder = undefined;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchFlaggedRecords.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchFlaggedRecords.fulfilled, (state, action) => {
        state.loading = false;
        const rawData = action.payload.data || [];
        state.data = rawData.map((item: any) => {
          // Already a proper array — nothing to do
          if (Array.isArray(item.deduplication)) {
            return item;
          }
          // deduplication is a raw JSON string — parse it
          if (typeof item.deduplication === 'string' && item.deduplication) {
            try {
              const parsed = JSON.parse(item.deduplication);
              return { ...item, deduplication: Array.isArray(parsed) ? parsed : [] };
            } catch {
              return item;
            }
          }
          // Fall back to deduplicationJson (may be a flat array or a wrapped object)
          if (item.deduplicationJson) {
            try {
              const parsed = JSON.parse(item.deduplicationJson);
              if (Array.isArray(parsed)) {
                return { ...item, deduplication: parsed };
              }
              if (parsed && Array.isArray(parsed.deduplication)) {
                return { ...item, deduplication: parsed.deduplication };
              }
              return { ...item, deduplication: [] };
            } catch {
              return item;
            }
          }
          return item;
        });
        state.totalRecords = action.payload.totalRecords || 0;
        state.lastUpdated = new Date().toISOString();
      })
      .addCase(fetchFlaggedRecords.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(fetchFlaggedRecordsDropdownData.pending, (state) => {
        state.dropdownLoading = true;
        state.dropdownError = null;
      })
      .addCase(fetchFlaggedRecordsDropdownData.fulfilled, (state, action) => {
        state.dropdownLoading = false;
        state.dropdownData = action.payload;
      })
      .addCase(fetchFlaggedRecordsDropdownData.rejected, (state, action) => {
        state.dropdownLoading = false;
        state.dropdownError = action.payload as string;
      });
  },
});

export const {
  updateFlaggedRecord,
  deleteFlaggedRecord,
  addFlaggedRecord,
  deleteFlaggedRecordByPatientId,
  setFilters,
  setPage,
  setPageSize,
  setSorting,
  clearFlaggedRecordsData,
} = flaggedRecordsSlice.actions;

export default flaggedRecordsSlice.reducer;
